import { Box, Grid, Typography } from '@mui/material'
import Tooltip, { TooltipProps, tooltipClasses } from '@mui/material/Tooltip'
import { styled } from '@mui/material/styles'
import { LeftPanelSection, RoundButtonChartTypeATMOT } from 'components/common/LeftPanel/LeftPanel.styled'
import { COVERAGE_RANGES, DATA_FILE_NAME, DATA_FILE_PATH, VARIABLE_LIST } from 'constant'
import { uniqueId } from 'lodash'
import { FC, Fragment, PropsWithChildren, useCallback, useMemo, useState } from 'react'
import { useDataWithMetric } from 'utils'
import { H3 } from '../MethodologyAndMetrica.styled'
import { ColorBox, IndicatorTD, ValueRange, ValueRangeValue, YearTD } from './CoverageChart.styled'
import { formatNumberDecimal } from 'utils/formatNumber'

const HtmlTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: '#FAFAFA',
    color: 'rgba(0, 0, 0, 0.87)',
    maxWidth: 220,
    fontSize: '14px',
    border: '1px solid #dadde9',
    fontFamily: 'CircularStdBook, sans-serif',
  },
}))

const TableWrapper: FC<PropsWithChildren> = ({ children }) => (
  <Box className={'coverage-tbl-wrapper'} display={'flex'} flexDirection={'row'} sx={{ overflowX: 'auto' }}>
    {children}
  </Box>
)

const CoverageChart = () => {
  const [chartType, setChartType] = useState<'country' | 'indicator'>('country')
  const csvFullData = useDataWithMetric(`${DATA_FILE_PATH}${DATA_FILE_NAME}.csv`)
  const variableList = useDataWithMetric(`${DATA_FILE_PATH}${VARIABLE_LIST}.csv`)

  /** BASED ON FULL DATA */
  const indicatorsList = useMemo(() => {
    const list =
      variableList
        ?.filter((ind: any) => ind?.display_name && ind?.display_data_source && ind?.pillar && ind?.pillar_color_code)
        ?.map((ind: any) => ({
          ...ind,
          // norm_column_name: `norm_${ind?.column_name}_display`,
          norm_column_name: `${ind?.column_name}`,
          indicator: ind.display_name,
        }))
        ?.sort((a: any, b: any) => a?.indicator?.localeCompare(b?.indicator)) || []
    return list
  }, [variableList])

  const countryChartData = useMemo(() => {
    const countryMap: any = {}

    indicatorsList.forEach((ind: any) => {
      csvFullData?.forEach((row: any) => {
        const code = row?.Code
        const year = row!.PublishYear
        const countryName = row?.CountryName
        const noData = isNaN(parseFloat(row[ind.norm_column_name]))
        if (!countryMap.hasOwnProperty(code)) {
          countryMap[code] = {
            title: countryName,
            country: countryName,
            [year]: noData ? null : 1,
          }
        } else {
          countryMap[code] = {
            ...countryMap[code],
            [year]: (countryMap[code][year] ?? 0) + (noData ? 0 : 1),
          }
        }
      })
    })
    const extractedData = Object.values(countryMap)

    return extractedData.filter((i: any) => i.title && i.country)
  }, [csvFullData, indicatorsList])

  const indicatorChartData = useMemo(() => {
    const indicatorMap: Record<string, any> = {}
    indicatorsList.forEach((ind: any) => {
      const columnName = ind.norm_column_name
      const displayName = ind.display_name
      csvFullData?.forEach((row: any) => {
        const year = row!.PublishYear
        const noData = isNaN(parseFloat(row[ind.norm_column_name]))
        if (!indicatorMap.hasOwnProperty(columnName)) {
          indicatorMap[columnName] = {
            title: displayName,
            [year]: noData ? 0 : 1,
          }
        } else {
          indicatorMap[columnName] = {
            ...indicatorMap[columnName],
            [year]: (indicatorMap[columnName][year] ?? 0) + (noData ? 0 : 1),
          }
        }
      })
    })

    let extractedData = Object.values(indicatorMap)
    extractedData = extractedData.map((i) => {
      let obj: any
      Object.keys(i).forEach((j) => {
        if (j !== '') {
          obj = { ...obj, [`${j}`]: i[j] }
        }
      })
      return obj
    })

    return extractedData
  }, [csvFullData, indicatorsList])

  const chartData = useMemo(() => {
    return chartType === 'country' ? countryChartData : indicatorChartData
  }, [chartType, countryChartData, indicatorChartData])

  const getColor = useCallback((value: string | number) => {
    const coverage = Number(value)
    if (coverage >= 86) return '#132E69'
    if (coverage >= 76) return '#428BC5'
    if (coverage > 0) return '#79CCEC'
    return '#E7E7E7'
  }, [])

  const yearList = useMemo(() => {
    const yearMap: any = {}
    csvFullData?.forEach((row: any) => {
      const year = row.PublishYear
      if (!yearMap[year]) {
        yearMap[year] = true
      }
    })
    return (
      Object.keys(yearMap)
        .map((key) => ({
          title: key,
          titleText: key,
          key,
        }))
        .filter((i) => i.title !== '' && i.titleText !== '' && i.key !== '')
        .sort((a: any, b: any) => a?.title?.localeCompare(b?.title)) || []
    )
  }, [csvFullData])

  return (
    <Box>
      <H3>Data Coverage (%) Per Year</H3>
      <Grid container spacing={2}>
        <Grid
          item
          xs={12}
          md={12}
          lg={10}
          xl={8}
          sx={{
            width: '100%',
            minWidth: { xs: '360px', sm: '400px', md: '480px', lg: '480px' },
          }}
        >
          <Box width={'100%'} maxWidth={{ lg: '480px' }}>
            <LeftPanelSection header='Chart type'>
              <Box sx={{ display: 'flex', gap: '16px', margin: '8px 0px' }}>
                <RoundButtonChartTypeATMOT
                  $chartType='country'
                  $currentType={chartType}
                  onClick={() => setChartType('country')}
                >
                  BY COUNTRY
                </RoundButtonChartTypeATMOT>
                <RoundButtonChartTypeATMOT
                  $chartType='indicator'
                  $currentType={chartType}
                  onClick={() => setChartType('indicator')}
                >
                  BY INDICATOR
                </RoundButtonChartTypeATMOT>
              </Box>
            </LeftPanelSection>
            <LeftPanelSection header='Coverage %'>
              <Fragment />
            </LeftPanelSection>
          </Box>
          <Grid container gap={'20px'} padding={'20px 0'}>
            {COVERAGE_RANGES.map((c) => (
              <Grid item key={uniqueId('coverage_range_')} xs={4} sm={2} md={1} lg={1}>
                <ValueRange $bg={c.color}>
                  <ValueRangeValue>{c.title}</ValueRangeValue>
                </ValueRange>
              </Grid>
            ))}
          </Grid>
        </Grid>
      </Grid>

      <TableWrapper>
        <table style={{ borderCollapse: 'collapse' }}>
          <tr>
            <th />
            {yearList.map((y: any) => (
              <th key={y?.titleText}>
                <Box width={'100%'} display={'flex'} justifyContent={'center'}>
                  <YearTD>{y?.titleText}</YearTD>
                </Box>
              </th>
            ))}
          </tr>
          <tbody>
            {chartData?.map((row: any) =>
              !row?.['title'] ? null : (
                <tr key={uniqueId('t_row_')}>
                  <IndicatorTD>{row.title}</IndicatorTD>
                  {yearList?.map((c: any) => {
                    return (
                      <td key={uniqueId('td_')} style={{ padding: '5px 3px' }}>
                        <HtmlTooltip
                          title={
                            Number(row?.[c?.key])
                              ? chartType === 'indicator'
                                ? `${formatNumberDecimal((row?.[c?.key] / countryChartData.length) * 100, 2)}%`
                                : `${formatNumberDecimal((row?.[c?.key] / indicatorsList.length) * 100)}%`
                              : 'No data'
                          }
                          placement='bottom-end'
                          followCursor
                          slotProps={{
                            popper: {
                              modifiers: [
                                {
                                  name: 'offset',
                                  options: {
                                    offset: [80, 5],
                                  },
                                },
                              ],
                            },
                          }}
                        >
                          <ColorBox
                            $bg={
                              chartType === 'country'
                                ? getColor(((row?.[c?.key] as number) / indicatorsList.length) * 100)
                                : getColor(((row?.[c?.key] as number) / countryChartData.length) * 100)
                            }
                          />
                        </HtmlTooltip>
                      </td>
                    )
                  })}
                </tr>
              ),
            )}
          </tbody>
        </table>
      </TableWrapper>
    </Box>
  )
}

export default CoverageChart
